/**
 * Mutually exclusive window fields for analytics APIs (trade bundles, reports/generate, trade-policy simulate).
 * Backend: do not mix `local_start_date`/`local_end_date` (+ timezone) with ISO `start_date`/`end_date`, or with `timeframe` when using explicit bounds.
 *
 * Align with {@link resolveTradeListWindowQuery} / statistics list filters so the same dashboard date state hits every route.
 */

import { resolveTradeListWindowQuery, type TradeListWindowQuery } from "./notificationPeriod";

export type AnalyticsWindowSnakeFields = {
  start_date?: string;
  end_date?: string;
  timeframe?: string;
  local_start_date?: string;
  local_end_date?: string;
  /** IANA zone; POST bodies also accept `reporting_timezone` as an alias on some routes — we standardize on `timezone`. */
  timezone?: string;
};

/**
 * Maps a resolved trade-list window to snake_case JSON/query fields. Returns only one style (local XOR iso XOR timeframe).
 */
export function analyticsWindowFieldsFromTradeListWindow(w: TradeListWindowQuery): AnalyticsWindowSnakeFields {
  if (w.kind === "local") {
    const out: AnalyticsWindowSnakeFields = {
      local_start_date: w.period.local_start_date,
      local_end_date: w.period.local_end_date,
    };
    if (w.period.timezone) out.timezone = w.period.timezone;
    return out;
  }
  return { timeframe: w.timeframe };
}

/**
 * Ensures `timezone` is set when using a local calendar window (matches statistics list filter behavior).
 */
export function withDefaultTimezoneForLocalWindow(
  w: TradeListWindowQuery,
  fields: AnalyticsWindowSnakeFields,
  reportingTimeZone: string
): AnalyticsWindowSnakeFields {
  if (w.kind !== "local") return fields;
  const z = reportingTimeZone?.trim();
  if (!z) return fields;
  if (fields.timezone) return fields;
  return { ...fields, timezone: z };
}

/**
 * Single entry point: same resolution as Statistics (local calendar + optional timezone, or preset timeframe).
 * Use for GET query objects and POST JSON bodies — spread the result; do not add ISO dates when `local_*` are set.
 */
export function buildAnalyticsWindowSnakeFields(
  dateRange: { startDate: Date | null; endDate: Date | null },
  timeframe: string,
  reportingTimeZone: string
): AnalyticsWindowSnakeFields {
  const w = resolveTradeListWindowQuery(dateRange, timeframe, reportingTimeZone, { includeTimezoneQuery: true });
  return withDefaultTimezoneForLocalWindow(w, analyticsWindowFieldsFromTradeListWindow(w), reportingTimeZone);
}
